import {
  type EventKind,
  isPollOpen,
  todayIso,
  useClubMemberships,
  useClubs,
  useEvents,
  useMenu,
  usePolls,
} from '@shkola/api';
import type { SubjectIcon } from '@shkola/design';
import { Link } from '@tanstack/react-router';
import { FileSignature, type LucideIcon, Utensils, Vote } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useCurrentContext } from '../app/ViewContext';
import { NAV, type NavKey } from '../app/nav';
import { useNavLayout } from '../app/Sidebar';
import { BirthdaysBlock } from '../components/school-life';
import {
  EventArt,
  GRAD,
  type Grad,
  INK,
  INK_SOFT,
  PageHead,
  SubjectArt,
  grad,
  ringSmall,
} from '../design/forms';
import { ArrowUpRightIcon } from '../design/icons';
import { ClassPetCard } from '../student/PetAndBackpack';
import { PuzzleCard } from '../student/PuzzleCard';
import { ReviewCard } from '../student/ReviewCard';
import { LifeArt, type LifeArtKind } from '../student/LifeArt';
import { forStudent } from '../student/home/GlassDay';

/*
 * «Шкільне життя» in «Форми 2» (28.10): the sections one opens now and then — no folders and stickers
 * any more — as white tiles on the paper, each with its picture in our one line on a soft square of its
 * colour, the ↗ circle and, where there is something to say, one line of it (the next event, today's
 * lunch, my clubs, the open poll); the birthdays of the week; for the student the class's own things.
 */

/** A section's picture: one of ours, or a line icon where we have none, on its colour. */
type Pic = { tone: Grad } & (
  { life: LifeArtKind } | { event: EventKind } | { subject: SubjectIcon } | { icon: LucideIcon }
);

const PICS: Partial<Record<NavKey, Pic>> = {
  season: { tone: 'lavender', life: 'rocket' },
  rewards: { tone: 'lime', life: 'crown' },
  achievements: { tone: 'apricot', event: 'exam' },
  album: { tone: 'sky', life: 'photo' },
  yearbook: { tone: 'cobalt', subject: 'BookOpen' },
  forms: { tone: 'sand', icon: FileSignature },
  letters: { tone: 'butter', life: 'letter' },
  clubs: { tone: 'coral', subject: 'Dumbbell' },
  events: { tone: 'pink', event: 'celebration' },
  canteen: { tone: 'mint', icon: Utensils },
  polls: { tone: 'aqua', icon: Vote },
  moving: { tone: 'sand', event: 'trip' },
};

function Picture({ pic }: { pic: Pic }) {
  const art =
    'life' in pic ? (
      <LifeArt kind={pic.life} tone={pic.tone} size={42} />
    ) : 'event' in pic ? (
      <EventArt kind={pic.event} tone={pic.tone} size={42} />
    ) : 'subject' in pic ? (
      <SubjectArt icon={pic.subject} tone={pic.tone} size={42} />
    ) : (
      <pic.icon size={26} strokeWidth={1.8} aria-hidden />
    );
  return (
    <span
      className="flex size-[60px] shrink-0 items-center justify-center rounded-[18px] transition-transform duration-300 ease-spring group-hover:scale-105"
      style={{ backgroundImage: grad(pic.tone), color: GRAD[pic.tone].ink }}
    >
      {art}
    </span>
  );
}

/** One line of what is there now: the next event, today's lunch, my clubs, the open poll. */
function useNews(): Partial<Record<NavKey, string>> {
  const ctx = useCurrentContext();
  const today = todayIso();
  const events = useEvents(ctx.schoolId, today);
  const menu = useMenu(ctx.schoolId, today, today);
  const clubs = useClubs(ctx.schoolId);
  const family = ctx.kind === 'student' || ctx.kind === 'guardian';
  const memberships = useClubMemberships(family ? [ctx.child.memberId] : []);
  const polls = usePolls(ctx.schoolId);
  const shown =
    ctx.kind === 'student' ? (events.data ?? []).filter(forStudent(ctx)) : (events.data ?? []);
  const next = shown[0];
  const lunch = (menu.data ?? []).filter((i) => i.meal === 'lunch');
  const mine = (memberships.data ?? [])
    .filter((m) => m.status === 'active')
    .map((m) => clubs.data?.find((c) => c.id === m.clubId)?.title)
    .filter(Boolean);
  const poll = (polls.data ?? []).find((p) => isPollOpen(p));
  return {
    events: next?.title,
    canteen: lunch.length > 0 ? lunch.map((i) => i.title).join(' · ') : undefined,
    clubs: mine.length > 0 ? mine.join(', ') : undefined,
    polls: poll?.question,
  };
}

export function LifePage() {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  const { hub } = useNavLayout();
  const news = useNews();
  const student = ctx.kind === 'student' ? ctx : undefined;

  return (
    <div className="flex flex-col gap-4">
      <PageHead title={t('nav.life')} sub={t('life.hint')} />
      <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
        {hub.map((key) => {
          const pic = PICS[key];
          if (!pic) return null;
          const line = news[key];
          return (
            <li key={key} className="min-w-0">
              <Link
                to={NAV[key].to}
                className="group flex h-full min-h-[140px] flex-col rounded-[24px] bg-white p-4 sm:min-h-[172px] transition-transform duration-300 ease-spring hover:-translate-y-0.5 md:p-5"
              >
                <span className="flex items-start justify-between gap-2">
                  <Picture pic={pic} />
                  <span
                    className={`${ringSmall} group-hover:bg-[var(--sh-dark)] group-hover:text-white`}
                  >
                    <ArrowUpRightIcon size={14} weight="bold" aria-hidden />
                  </span>
                </span>
                <span
                  className="mt-auto pt-4 font-display text-[20px] leading-[25px] font-light tracking-[-0.02em]"
                  style={{ color: INK }}
                >
                  {t(`nav.${key}`)}
                </span>
                {line && (
                  <span
                    className="mt-1 line-clamp-2 text-[13px] leading-[18px]"
                    style={{ color: INK_SOFT }}
                  >
                    {line}
                  </span>
                )}
              </Link>
            </li>
          );
        })}
      </ul>

      <BirthdaysBlock schoolId={ctx.schoolId} />

      {student && (
        <section className="mt-2 flex flex-col gap-4">
          <h2
            className="pt-2 font-display text-[30px] leading-[36px] font-light tracking-[-0.035em]"
            style={{ color: INK }}
          >
            {t('life.class', { class: student.child.class?.label ?? '' })}
          </h2>
          <div className="grid items-start gap-4 md:grid-cols-2 xl:grid-cols-3">
            {student.child.class && (
              <ClassPetCard
                classId={student.child.class.id}
                classLabel={student.child.class.label}
              />
            )}
            <PuzzleCard schoolId={student.schoolId} studentId={student.child.memberId} />
            <ReviewCard studentId={student.child.memberId} />
          </div>
        </section>
      )}
    </div>
  );
}
